import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, openProposalPdf } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  FileSignature, ChevronRight, ChevronLeft, Search, Plus,
  Film, Newspaper, Package, AlertTriangle, CheckCircle2, MessageCircle,
} from 'lucide-react';
import ProposalConditionsEditor from '@/components/ProposalConditionsEditor';
import ProposalLineEditor from '@/components/ProposalLineEditor';

const STEPS = [
  { key: 'client', label: 'Client' },
  { key: 'services', label: 'Services' },
  { key: 'lines', label: 'Pricing' },
  { key: 'terms', label: 'Terms' },
  { key: 'review', label: 'Review' },
];

function fmtINR(n) {
  if (n == null) return '—';
  try { return `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`; }
  catch { return `₹${n}`; }
}

function isFriday(iso) {
  if (!iso) return true;
  try {
    const [y, m, d] = iso.split('-').map(Number);
    // UTC to avoid local timezone surprises on cross-tz machines.
    return new Date(Date.UTC(y, m - 1, d)).getUTCDay() === 5;
  } catch { return true; }
}

function nextFriday(iso) {
  // Smallest Friday ≥ iso. Returns the same date if it IS a Friday.
  if (!iso) return '';
  try {
    const [y, m, d] = iso.split('-').map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d));
    const fwd = (5 - dt.getUTCDay() + 7) % 7;    // days until next Friday (Fri=5 in getUTCDay)
    dt.setUTCDate(dt.getUTCDate() + fwd);
    return dt.toISOString().slice(0, 10);
  } catch { return ''; }
}

function prevFriday(iso) {
  if (!iso) return '';
  try {
    const [y, m, d] = iso.split('-').map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d));
    const back = (dt.getUTCDay() - 5 + 7) % 7;
    dt.setUTCDate(dt.getUTCDate() - back);
    return dt.toISOString().slice(0, 10);
  } catch { return ''; }
}

function weekdayName(iso) {
  if (!iso) return '';
  try {
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', { weekday: 'long', timeZone: 'UTC' });
  } catch { return ''; }
}

export default function NewProposalPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id: editId } = useParams();  // when present → editing an existing proposal
  const isEdit = !!editId;
  const [editing, setEditing] = useState(null);  // the existing proposal (edit mode)
  const [step, setStep] = useState(0);
  const [services, setServices] = useState([]);
  const [clients, setClients] = useState([]);
  const [draft, setDraft] = useState({
    client_id: '',
    client_name: '',
    contact_name: '',
    to_emails: [],
    services: [],
    lines: [],
    campaign_date_from: '',
    campaign_date_to: '',
    validity_date: '',
    discount_pct: 0,
    gst_pct: 18,
    subject_line: 'Advertisement Proposal',
    conditions_snapshot: [],  // ADD-ON: editable per-proposal clauses
    prepared_by: {
      name: user?.name || '',
      designation: user?.role || '',
      phone: user?.phone || '',
      email: user?.email || '',
    },
  });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  // Lines edited since the last save: amounts shown are stale until saved.
  const [linesDirty, setLinesDirty] = useState(false);

  // Theatre picker state
  const [theatreNetwork, setTheatreNetwork] = useState('QUBE');
  const [theatreQuery, setTheatreQuery] = useState('');
  const [theatreRows, setTheatreRows] = useState([]);
  const [theatreTotal, setTheatreTotal] = useState(0);
  const [theatrePage, setTheatrePage] = useState(1);
  const [defaultRateType, setDefaultRateType] = useState('normal');
  const [defaultScreens, setDefaultScreens] = useState(1);
  const [defaultWeeks, setDefaultWeeks] = useState(1);
  const [defaultDuration, setDefaultDuration] = useState(10);
  const [theatreCat, setTheatreCat] = useState({ duration_options: [10, 20, 30, 40, 50, 60], base_duration_seconds: 10 });
  useEffect(() => {
    api.get('/rate-catalogs/theatre').then((r) => setTheatreCat(r.data || {})).catch(() => {});
  }, []);

  // Newspaper line state (single builder)
  const [npLine, setNpLine] = useState({
    newspaper: 'Eenadu', edition: '', edition_type: 'District', colour: 'Colour',
    width_cm: 10, height_cm: 5, rate_per_sqcm: 700, insertions: 1,
  });

  // Custom line state
  const [cmLine, setCmLine] = useState({ item: '', description: '', quantity: 1, rate: 0 });

  useEffect(() => {
    (async () => {
      try {
        const [svc, cls, terms] = await Promise.all([
          api.get('/services'),
          api.get('/clients'),
          api.get('/terms/current', { params: { kind: 'default' } }).catch(() => ({ data: {} })),
        ]);
        setServices(svc.data);
        setClients(cls.data);
        // Seed per-proposal conditions_snapshot from the active master Terms.
        // Parses "1. Clause ...\n2. Clause ..." into a clean list the user can
        // reorder / tick / edit on the Review step without touching master Terms.
        const body = terms?.data?.body || '';
        const clauses = body
          .split(/\n+/)
          .map((s) => s.replace(/^\s*\d{1,3}\s*[.)]\s+/, '').trim())
          .filter((s) => s && !/sample|settings\s*→|rate catalog/i.test(s))
          .map((text, i) => ({
            id: (typeof crypto !== 'undefined' && crypto.randomUUID
              ? crypto.randomUUID()
              : Math.random().toString(36).slice(2)),
            order: i + 1,
            text,
            source: 'master',
            included: true,
          }));
        setDraft((d) => (d.conditions_snapshot?.length ? d : { ...d, conditions_snapshot: clauses }));
      } catch { /* ignore */ }
    })();
  }, []);

  // ─── Edit mode: load the existing proposal and prefill the draft ───
  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await api.get(`/proposals/${editId}`);
        setEditing(data);
        // Prefill the editor state from the existing proposal.
        const services = Array.from(new Set((data.lines || []).map((l) => l.service_key).filter(Boolean)));
        setDraft({
          client_id: data.client_id || '',
          client_name: data.client_name || '',
          contact_name: data.contact_name || '',
          to_emails: data.to_emails || [],
          services,
          lines: data.lines || [],
          campaign_date_from: data.campaign_date_from || '',
          campaign_date_to: data.campaign_date_to || '',
          validity_date: data.validity_date || '',
          discount_pct: data.discount_pct || 0,
          gst_pct: data.gst_pct ?? 18,
          subject_line: data.subject_line || 'Advertisement Proposal',
          conditions_snapshot: data.conditions_snapshot || [],
          prepared_by: data.prepared_by || {
            name: user?.name || '', designation: user?.role || '',
            phone: user?.phone || '', email: user?.email || '',
          },
        });
        setPreview({ id: data.id, number: data.number });  // enables PDF/Open buttons
        setStep(2);  // jump straight to Pricing (most common edit target)
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Could not load this proposal');
      }
    })();
    // eslint-disable-next-line
  }, [editId]);

  const loadTheatres = async () => {
    try {
      const { data } = await api.get(`/rate-items?catalog_key=theatre&network=${encodeURIComponent(theatreNetwork)}&q=${encodeURIComponent(theatreQuery)}&page=${theatrePage}&size=25`);
      setTheatreRows(data.rows); setTheatreTotal(data.total);
    } catch { toast.error('Could not load theatres'); }
  };
  useEffect(() => { if (draft.services.includes('theatre_ads')) loadTheatres(); // eslint-disable-next-line
  }, [theatreNetwork, theatrePage, draft.services]);

  const toggleService = (key) => {
    const has = draft.services.includes(key);
    setDraft({ ...draft, services: has ? draft.services.filter((k) => k !== key) : [...draft.services, key] });
  };

  const addTheatreLine = (ri) => {
    const already = draft.lines.find((ln) => ln.rate_item_id === ri.id);
    if (already) return toast('Already added');
    const line = {
      service_key: 'theatre_ads',
      rate_item_id: ri.id,
      network: ri.network,
      rate_type: defaultRateType,
      screens: defaultScreens,
      weeks: defaultWeeks,
      duration_seconds: defaultDuration,
      attributes: ri.attributes || {},
    };
    setDraft({ ...draft, lines: [...draft.lines, line] });
    toast.success(`Added: ${ri.attributes?.theatre || ri.attributes?.theatre_code}`);
  };

  // Update a specific field on an existing line.
  const patchLine = (idx, patch) => {
    const next = [...draft.lines];
    next[idx] = { ...next[idx], ...patch };
    setDraft({ ...draft, lines: next });
    setLinesDirty(true);
  };

  const removeLine = (i) => {
    const next = [...draft.lines];
    next.splice(i, 1);
    setDraft({ ...draft, lines: next });
    setLinesDirty(true);
  };

  const addNewspaperLine = () => {
    setDraft({ ...draft, lines: [...draft.lines, { service_key: 'newspaper_eenadu', ...npLine }] });
  };

  const addCustomLine = () => {
    if (!cmLine.item && !cmLine.description) return toast.error('Item name is required');
    // Shape: {item (name shown as item), description (optional multi-line, prints smaller under)}
    // Backward compat: if user only typed in the (old) single field, we store it as item.
    const line = {
      service_key: 'custom',
      item: (cmLine.item || cmLine.description || '').trim(),
      description: cmLine.item ? (cmLine.description || '').trim() : '',
      quantity: cmLine.quantity,
      rate: cmLine.rate,
    };
    setDraft({ ...draft, lines: [...draft.lines, line] });
    setCmLine({ item: '', description: '', quantity: 1, rate: 0 });
  };

  const doPreview = async () => {
    // Save draft to get server-side totals (temporarily)
    if (!draft.client_name) return toast.error('Pick a client');
    if (!draft.prepared_by.name || !draft.prepared_by.phone) {
      setStep(0);
      return toast.error('Add your name and phone to continue');
    }
    setBusy(true);
    try {
      if (isEdit && editing?.id) {
        // Edit mode: PATCH existing proposal, keep same number.
        const { data } = await api.patch(`/proposals/${editing.id}`, draft);
        setPreview(data);
        setEditing(data);
        setDraft((d) => ({ ...d, lines: data.lines || d.lines, discount_pct: data.discount_pct ?? d.discount_pct }));
        setLinesDirty(false);
        toast.success(`Saved ${data.number} (v${data.version_counter || 1})`);
      } else {
        const { data } = await api.post('/proposals', draft);
        setPreview(data);
        toast.success(`Draft saved as ${data.number}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save');
    } finally { setBusy(false); }
  };

  const openPdf = () => {
    if (!preview) return;
    openProposalPdf(preview.id, {
      mode: 'open',
      filename: `${preview.number || 'proposal'}.pdf`,
      onError: (m) => toast.error(m),
    });
  };

  // Required-field map per step; drives the * marks AND the per-field error
  // highlights that appear when the user hits Next without filling them.
  const missingByStep = useMemo(() => {
    const miss = { 0: [], 1: [], 2: [] };
    // Step 0 — Client & Prepared-by
    if (!draft.client_name)         miss[0].push({ field: 'client_name',   label: 'Client' });
    if (!draft.validity_date)       miss[0].push({ field: 'validity_date', label: 'Valid till' });
    if (!draft.prepared_by.name)    miss[0].push({ field: 'prep_name',     label: 'Prepared-by name' });
    if (!draft.prepared_by.phone)   miss[0].push({ field: 'prep_phone',    label: 'Prepared-by phone' });
    // Step 1 — Services
    if (draft.services.length === 0) miss[1].push({ field: 'services', label: 'Pick at least one service' });
    // Step 2 — Lines
    if (draft.lines.length === 0)    miss[2].push({ field: 'lines',    label: 'Add at least one line item' });
    return miss;
  }, [draft]);
  const [showMissing, setShowMissing] = useState(false);
  const stepMissing = missingByStep[step] || [];
  const canNext = stepMissing.length === 0;

  const next = () => {
    if (!canNext) { setShowMissing(true); return; }
    setShowMissing(false);
    setStep(Math.min(step + 1, STEPS.length - 1));
  };
  const back = () => { setShowMissing(false); setStep(Math.max(step - 1, 0)); };

  return (
    <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-6 space-y-4" data-testid="new-proposal-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-ink flex items-center gap-2">
          <FileSignature className="h-6 w-6 text-green-ink" />
          {isEdit ? `Edit proposal ${editing?.number || ''}` : 'New proposal'}
          {isEdit && editing?.status && (
            <Badge className="ml-2 bg-slate-100 text-slate-700 border-0 text-xs">{editing.status}</Badge>
          )}
        </h1>
        <Button variant="ghost" onClick={() => navigate(isEdit && editing?.id ? `/proposals/${editing.id}` : '/proposals')}>Cancel</Button>
      </div>

      {/* Steps bar */}
      <Card className="shine-card p-3 rounded-xl">
        <div className="flex flex-wrap items-center gap-2">
          {STEPS.map((s, i) => (
            <button key={s.key} onClick={() => setStep(i)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
                      i === step ? 'bg-ink text-white' :
                      i < step ? 'bg-green-tint text-green-ink' : 'bg-page text-ink-2 hover:text-ink'
                    }`}
                    data-testid={`step-${s.key}`}>
              {i + 1}. {s.label}
            </button>
          ))}
        </div>
      </Card>

      {/* Step 0: Client */}
      {step === 0 && (
        <Card className="shine-card p-5 rounded-xl space-y-4">
          <h2 className="text-sm font-semibold text-ink">Client & Prepared-by</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Client <span className="text-rose-500">*</span></Label>
              <Select value={draft.client_id}
                      onValueChange={(cid) => {
                        const c = clients.find((x) => x.id === cid);
                        setDraft({ ...draft, client_id: cid, client_name: c?.company || c?.name || '', contact_name: c?.contact_name || '' });
                      }}>
                <SelectTrigger data-testid="np-client"
                               className={showMissing && !draft.client_name ? 'border-rose-400 focus:ring-rose-200' : ''}>
                  <SelectValue placeholder="Pick a client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.company || c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Contact person</Label>
              <Input value={draft.contact_name} onChange={(e) => setDraft({ ...draft, contact_name: e.target.value })} />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">Subject line</Label>
              <Input value={draft.subject_line} onChange={(e) => setDraft({ ...draft, subject_line: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Campaign from (Friday)</Label>
              <Input type="date" value={draft.campaign_date_from}
                     onChange={(e) => setDraft({ ...draft, campaign_date_from: e.target.value })}
                     data-testid="np-campaign-from" />
              {draft.campaign_date_from && !isFriday(draft.campaign_date_from) && (
                <div className="mt-1 flex items-start gap-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-2"
                     data-testid="np-friday-warning">
                  <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                  <div className="space-y-1.5">
                    <div>Theatre ads sell in Fri → Thu weeks. You picked a <b>{weekdayName(draft.campaign_date_from)}</b>.</div>
                    <div className="flex gap-1.5">
                      <Button type="button" size="sm" variant="outline"
                              onClick={() => setDraft({ ...draft, campaign_date_from: nextFriday(draft.campaign_date_from) })}
                              className="h-7 text-[11px] border-amber-400 text-amber-800 hover:bg-amber-100"
                              data-testid="np-use-next-friday">
                        Use next Friday ({nextFriday(draft.campaign_date_from)})
                      </Button>
                      <Button type="button" size="sm" variant="ghost"
                              className="h-7 text-[11px] text-amber-700"
                              onClick={() => toast('Kept — pricing anchors on this exact date; no days before it are charged.')}
                              data-testid="np-keep-this-date">
                        Keep this date
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs">Campaign to</Label>
              <Input type="date" value={draft.campaign_date_to}
                     onChange={(e) => setDraft({ ...draft, campaign_date_to: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Valid till <span className="text-rose-500">*</span></Label>
              <Input type="date" value={draft.validity_date}
                     onChange={(e) => setDraft({ ...draft, validity_date: e.target.value })}
                     className={showMissing && !draft.validity_date ? 'border-rose-400 focus-visible:ring-rose-200' : ''}
                     data-testid="np-validity-date" />
            </div>
          </div>

          <div className="pt-3 border-t border-ink/10">
            <h3 className="text-xs font-semibold text-ink-2 uppercase mb-2">Prepared by (from your profile — editable)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Name <span className="text-rose-500">*</span></Label>
                <Input value={draft.prepared_by.name}
                       onChange={(e) => setDraft({ ...draft, prepared_by: { ...draft.prepared_by, name: e.target.value } })}
                       className={showMissing && !draft.prepared_by.name ? 'border-rose-400 focus-visible:ring-rose-200' : ''}
                       data-testid="np-prep-name" />
              </div>
              <div>
                <Label className="text-xs">Designation</Label>
                <Input value={draft.prepared_by.designation}
                       onChange={(e) => setDraft({ ...draft, prepared_by: { ...draft.prepared_by, designation: e.target.value } })} />
              </div>
              <div>
                <Label className="text-xs">Phone <span className="text-rose-500">*</span></Label>
                <Input value={draft.prepared_by.phone}
                       onChange={(e) => setDraft({ ...draft, prepared_by: { ...draft.prepared_by, phone: e.target.value } })}
                       className={showMissing && !draft.prepared_by.phone ? 'border-rose-400 focus-visible:ring-rose-200' : ''}
                       data-testid="np-prep-phone" />
              </div>
              <div>
                <Label className="text-xs">Email</Label>
                <Input value={draft.prepared_by.email}
                       onChange={(e) => setDraft({ ...draft, prepared_by: { ...draft.prepared_by, email: e.target.value } })} />
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Step 1: Services */}
      {step === 1 && (
        <Card className="shine-card p-5 rounded-xl space-y-3">
          <h2 className="text-sm font-semibold text-ink">Tick the services for this proposal</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {/* Product Catalog proposal — jumps to the dedicated wizard. */}
            {!isEdit && (
            <button key="product_catalog"
                    onClick={() => navigate('/proposals/new/catalog')}
                    className="p-4 rounded-xl border-2 text-left transition border-ink/10 bg-white hover:border-indigo-300 hover:bg-indigo-50/30"
                    data-testid="svc-card-product-catalog">
              <div className="flex items-center gap-2">
                <Package className="h-5 w-5 text-indigo-600" />
                <div className="font-semibold text-ink">Product Catalog proposal</div>
              </div>
              <div className="text-xs text-ink-2 mt-1">
                Digital marketing, creative, LED van, studio rental and more → dedicated wizard
              </div>
            </button>
            )}
            {services.map((s) => {
              const picked = draft.services.includes(s.key);
              const Icon =
                s.key === 'theatre_ads' ? Film :
                s.key === 'newspaper_eenadu' ? Newspaper :
                s.key === 'messaging_campaign' ? MessageCircle :
                Package;
              // Messaging has its own dedicated wizard (channels, SMS calculator,
              // compliance ticks) — hop over to it when the user picks that card.
              const handlePick = () => {
                if (s.key === 'messaging_campaign' && !isEdit) {
                  navigate('/proposals/new-messaging');
                  return;
                }
                toggleService(s.key);
              };
              return (
                <button key={s.id} onClick={handlePick}
                        className={`p-4 rounded-xl border-2 text-left transition ${picked ? 'border-green-ink bg-green-tint/40' : 'border-ink/10 bg-white hover:border-ink/30'}`}
                        data-testid={`svc-card-${s.key}`}>
                  <div className="flex items-center gap-2">
                    <Icon className="h-5 w-5 text-green-ink" />
                    <div className="font-semibold text-ink">{s.name}</div>
                    {picked && <CheckCircle2 className="h-4 w-4 ml-auto text-green-ink" />}
                  </div>
                  <div className="text-xs text-ink-2 mt-1">
                    {s.key === 'messaging_campaign'
                      ? 'Dedicated wizard →'
                      : s.pricing_mode === 'catalog' ? 'From rate catalog'
                      : s.pricing_mode === 'tariff' ? 'From tariff' : 'Manual rate'}
                  </div>
                </button>
              );
            })}
          </div>
        </Card>
      )}

      {/* Step 2: Lines */}
      {step === 2 && (
        <div className="space-y-4">
          {/* Theatre picker */}
          {draft.services.includes('theatre_ads') && (
            <Card className="shine-card p-5 rounded-xl">
              <div className="flex items-center gap-2 mb-3">
                <Film className="h-5 w-5 text-green-ink" />
                <h2 className="text-sm font-semibold text-ink">Theatre line items</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-3">
                <Select value={theatreNetwork} onValueChange={(v) => { setTheatreNetwork(v); setTheatrePage(1); }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="QUBE">QUBE</SelectItem>
                    <SelectItem value="UFO">UFO</SelectItem>
                    <SelectItem value="PVR INOX">PVR INOX</SelectItem>
                  </SelectContent>
                </Select>
                <div className="relative md:col-span-2">
                  <Search className="h-4 w-4 absolute left-2 top-2.5 text-ink-2" />
                  <Input value={theatreQuery} onChange={(e) => setTheatreQuery(e.target.value)}
                         onKeyDown={(e) => e.key === 'Enter' && (setTheatrePage(1), loadTheatres())}
                         placeholder="Search city / theatre / code"
                         className="pl-8" />
                </div>
                <Button variant="outline" onClick={() => { setTheatrePage(1); loadTheatres(); }}>Search</Button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3 text-xs">
                <div>
                  <Label className="text-[11px]">Default rate type</Label>
                  <Select value={defaultRateType} onValueChange={setDefaultRateType}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="bb">BB</SelectItem>
                      <SelectItem value="mbb">MBB</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[11px]">Default duration</Label>
                  <Select value={String(defaultDuration)} onValueChange={(v) => setDefaultDuration(+v)}>
                    <SelectTrigger data-testid="np-default-duration"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(theatreCat.duration_options || [10, 20, 30, 40, 50, 60]).map((d) => (
                        <SelectItem key={d} value={String(d)}>{d} sec</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[11px]">Default screens</Label>
                  <Input type="number" value={defaultScreens} onChange={(e) => setDefaultScreens(+e.target.value || 1)} />
                </div>
                <div>
                  <Label className="text-[11px]">Default weeks</Label>
                  <Input type="number" value={defaultWeeks} onChange={(e) => setDefaultWeeks(+e.target.value || 1)} />
                </div>
              </div>

              <div className="overflow-x-auto border border-ink/10 rounded-lg max-h-96 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-page">
                    <tr className="text-left text-ink-2 uppercase">
                      <th className="py-2 px-2">Code</th>
                      <th className="py-2 px-2">Theatre</th>
                      <th className="py-2 px-2">City</th>
                      <th className="py-2 px-2">Seats</th>
                      <th className="py-2 px-2">Normal</th>
                      <th className="py-2 px-2">BB</th>
                      <th className="py-2 px-2">MBB</th>
                      <th className="py-2 px-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {theatreRows.map((r) => (
                      <tr key={r.id} className="border-t border-ink/5">
                        <td className="py-1.5 px-2 font-mono">{r.attributes?.theatre_code}</td>
                        <td className="py-1.5 px-2">{r.attributes?.theatre}</td>
                        <td className="py-1.5 px-2">{r.attributes?.city}</td>
                        <td className="py-1.5 px-2">{r.attributes?.seats}</td>
                        <td className="py-1.5 px-2 tabular-nums">{r.rates?.normal ?? '—'}</td>
                        <td className="py-1.5 px-2 tabular-nums">{r.rates?.bb ?? '—'}</td>
                        <td className="py-1.5 px-2 tabular-nums">{r.rates?.mbb ?? '—'}</td>
                        <td className="py-1.5 px-2 text-right">
                          <Button size="sm" variant="outline" onClick={() => addTheatreLine(r)}
                                  data-testid={`add-theatre-${r.id}`}>
                            <Plus className="h-3 w-3" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {theatreRows.length === 0 && (
                      <tr><td colSpan={8} className="py-6 text-center text-ink-2">
                        No theatres. Import rate sheets first (Rate Catalog).
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              {theatreTotal > 25 && (
                <div className="flex items-center justify-between mt-2 text-xs">
                  <div className="text-ink-2">{theatreTotal} total · page {theatrePage}</div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" disabled={theatrePage === 1} onClick={() => setTheatrePage(theatrePage - 1)}>Prev</Button>
                    <Button size="sm" variant="outline" disabled={theatrePage * 25 >= theatreTotal} onClick={() => setTheatrePage(theatrePage + 1)}>Next</Button>
                  </div>
                </div>
              )}
            </Card>
          )}

          {/* Newspaper line builder */}
          {draft.services.includes('newspaper_eenadu') && (
            <Card className="shine-card p-5 rounded-xl">
              <div className="flex items-center gap-2 mb-3">
                <Newspaper className="h-5 w-5 text-green-ink" />
                <h2 className="text-sm font-semibold text-ink">Newspaper line</h2>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <div><Label className="text-xs">Newspaper</Label><Input value={npLine.newspaper} onChange={(e) => setNpLine({ ...npLine, newspaper: e.target.value })} /></div>
                <div><Label className="text-xs">Edition</Label><Input value={npLine.edition} onChange={(e) => setNpLine({ ...npLine, edition: e.target.value })} /></div>
                <div>
                  <Label className="text-xs">Edition type</Label>
                  <Select value={npLine.edition_type} onValueChange={(v) => setNpLine({ ...npLine, edition_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Main">Main</SelectItem>
                      <SelectItem value="District">District</SelectItem>
                      <SelectItem value="Combination">Combination</SelectItem>
                      <SelectItem value="Appointment">Appointment</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Colour</Label>
                  <Select value={npLine.colour} onValueChange={(v) => setNpLine({ ...npLine, colour: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Colour">Colour</SelectItem>
                      <SelectItem value="B&W">B&W</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">Width (cm)</Label><Input type="number" value={npLine.width_cm} onChange={(e) => setNpLine({ ...npLine, width_cm: +e.target.value || 0 })} /></div>
                <div><Label className="text-xs">Height (cm)</Label><Input type="number" value={npLine.height_cm} onChange={(e) => setNpLine({ ...npLine, height_cm: +e.target.value || 0 })} /></div>
                <div><Label className="text-xs">Rate / sq cm</Label><Input type="number" value={npLine.rate_per_sqcm} onChange={(e) => setNpLine({ ...npLine, rate_per_sqcm: +e.target.value || 0 })} /></div>
                <div><Label className="text-xs">Insertions</Label><Input type="number" value={npLine.insertions} onChange={(e) => setNpLine({ ...npLine, insertions: +e.target.value || 1 })} /></div>
              </div>
              <Button className="mt-3" onClick={addNewspaperLine} data-testid="add-newspaper-line">
                <Plus className="h-4 w-4 mr-1" /> Add newspaper line
              </Button>
            </Card>
          )}

          {/* Custom line */}
          {draft.services.includes('custom') && (
            <Card className="shine-card p-5 rounded-xl">
              <div className="flex items-center gap-2 mb-3">
                <Package className="h-5 w-5 text-green-ink" />
                <h2 className="text-sm font-semibold text-ink">Custom budget item</h2>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <div className="md:col-span-2">
                  <Label className="text-xs">Item name *</Label>
                  <Input
                    value={cmLine.item}
                    onChange={(e) => setCmLine({ ...cmLine, item: e.target.value })}
                    placeholder="e.g. Hoarding printing"
                    data-testid="custom-item-input"
                  />
                </div>
                <div>
                  <Label className="text-xs">Quantity</Label>
                  <Input type="number" value={cmLine.quantity} onChange={(e) => setCmLine({ ...cmLine, quantity: +e.target.value || 1 })} />
                </div>
                <div>
                  <Label className="text-xs">Rate (₹)</Label>
                  <Input type="number" value={cmLine.rate} onChange={(e) => setCmLine({ ...cmLine, rate: +e.target.value || 0 })} />
                </div>
                <div className="md:col-span-4">
                  <Label className="text-xs">
                    Description (optional, printed in smaller text under the item on the PDF)
                  </Label>
                  <textarea
                    className="w-full rounded-md border border-ink/10 bg-white p-2 text-sm"
                    rows={2}
                    value={cmLine.description}
                    onChange={(e) => setCmLine({ ...cmLine, description: e.target.value })}
                    placeholder="e.g. 10 ft × 20 ft flex banner printed on 440 GSM, with eyelets and ropes."
                    data-testid="custom-description-input"
                  />
                </div>
              </div>
              <Button className="mt-3" onClick={addCustomLine} data-testid="add-custom-line">
                <Plus className="h-4 w-4 mr-1" /> Add custom line
              </Button>
            </Card>
          )}

          {/* Lines added so far */}
          <Card className="shine-card p-5 rounded-xl">
            <h3 className="text-sm font-semibold text-ink mb-2">Lines added · {draft.lines.length}</h3>
            {draft.lines.length === 0 ? (
              <div className="text-sm text-ink-2">Pick line items above. Totals are shown on the Review step.</div>
            ) : (
              <div className="space-y-2">
                {draft.lines.map((ln, i) => (
                  <ProposalLineEditor key={i} line={ln} index={i}
                                      onChange={patchLine} onRemove={removeLine}
                                      durationOptions={theatreCat.duration_options || [10, 20, 30, 40, 50, 60]}
                                      dirty={linesDirty} />
                ))}
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Step 3: Terms */}
      {step === 3 && (
        <Card className="shine-card p-5 rounded-xl space-y-3">
          <h2 className="text-sm font-semibold text-ink">Commercials</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div><Label className="text-xs">Manual discount %</Label><Input type="number" value={draft.discount_pct} onChange={(e) => setDraft({ ...draft, discount_pct: +e.target.value || 0 })} data-testid="np-discount" /></div>
            <div><Label className="text-xs">GST %</Label><Input type="number" value={draft.gst_pct} onChange={(e) => setDraft({ ...draft, gst_pct: +e.target.value || 0 })} /></div>
            <div><Label className="text-xs">Payment terms</Label><Input value={draft.payment_terms || ''} onChange={(e) => setDraft({ ...draft, payment_terms: e.target.value })} placeholder="e.g. 50% advance, 50% before start" /></div>
          </div>
          <div>
            <Label className="text-xs">Notes</Label>
            <textarea className="w-full min-h-[80px] rounded-md border border-ink/15 p-2 text-sm bg-white"
                      value={draft.notes || ''} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} />
          </div>
        </Card>
      )}

      {/* Step 4: Review */}
      {step === 4 && (
        <div className="space-y-4">
          <Card className="shine-card p-5 rounded-xl space-y-4">
            <h2 className="text-sm font-semibold text-ink">Review & Generate</h2>
            {(!preview || isEdit) ? (
              <div className="space-y-2 text-sm">
                <div><b>Proposal number:</b> {editing?.number || '(new)'}</div>
                <div><b>Client:</b> {draft.client_name}</div>
                <div><b>Services:</b> {draft.services.join(', ')}</div>
                <div><b>Lines:</b> {draft.lines.length}</div>
                <div><b>Prepared by:</b> {draft.prepared_by.name} · {draft.prepared_by.phone}</div>
                {isEdit && preview?.subtotal != null && (
                  <div className="text-xs text-ink-2" data-testid="np-breakdown">
                    Subtotal {fmtINR(preview.subtotal)}
                    {preview.discount_pct ? <> · Discount {preview.discount_pct}%</> : null}
                    {preview.gst_amount != null && <> · GST {fmtINR(preview.gst_amount)}</>}
                  </div>
                )}
                {preview?.total != null && (
                  <div className="pt-1 text-lg text-green-ink font-bold" data-testid="np-total">
                    Total: {fmtINR(preview.total)}
                    {preview.version_counter > 1 && <span className="ml-2 text-xs text-ink-2 font-normal">v{preview.version_counter}</span>}
                  </div>
                )}
                <div className="flex gap-2 pt-2">
                  <Button onClick={doPreview} disabled={busy} data-testid="np-save-btn">
                    {busy ? 'Saving…' : (isEdit ? 'Save changes' : 'Save draft & compute totals')}
                  </Button>
                  {isEdit && preview?.id && (
                    <>
                      <Button variant="outline" onClick={openPdf} data-testid="np-pdf-btn">Download PDF</Button>
                      <Button variant="outline" onClick={() => navigate(`/proposals/${editing.id}`)}>Back to proposal</Button>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-green-ink">
                  <CheckCircle2 className="h-5 w-5" />
                  <div className="font-mono font-semibold">{preview.number}</div>
                  <Badge variant="outline">{preview.status}</Badge>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div className="space-y-1">
                    <div><b>Subtotal:</b> {fmtINR(preview.subtotal)}</div>
                    {preview.slab_discount_pct > 0 && (
                      <div><b>Slab discount:</b> {preview.slab_discount_pct}% ({preview.slab_label})</div>
                    )}
                    {preview.discount_pct > 0 && (
                      <div><b>Manual discount:</b> {preview.discount_pct}% = {fmtINR(preview.discount_amount)}</div>
                    )}
                    <div><b>GST @ {preview.gst_pct}%:</b> {fmtINR(preview.gst_amount)}</div>
                    <div className="text-lg font-bold text-green-ink"><b>Total:</b> {fmtINR(preview.total)}</div>
                  </div>
                  <div className="space-y-1 text-ink-2 text-xs">
                    <div><b>Prepared by:</b> {preview.prepared_by_name}, {preview.prepared_by_designation}</div>
                    <div>{preview.prepared_by_phone} · {preview.prepared_by_email}</div>
                  </div>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button onClick={openPdf} data-testid="np-pdf-btn">Download quotation PDF</Button>
                  <Button variant="outline" onClick={() => navigate(`/proposals/${preview.id}`)}>Open proposal</Button>
              </div>
            </div>
          )}
          </Card>
          {/* ADD-ON: Editable per-proposal conditions.
              Edits here only affect this proposal; master Terms stay intact. */}
          <ProposalConditionsEditor
            value={draft.conditions_snapshot}
            onChange={async (next) => {
              setDraft({ ...draft, conditions_snapshot: next });
              // When the proposal has already been saved (preview exists),
              // persist the snapshot change immediately so the next PDF
              // reflects the edits.
              if (preview?.id) {
                try {
                  await api.patch(`/proposals/${preview.id}`, { conditions_snapshot: next });
                } catch (e) {
                  toast.error(e?.response?.data?.detail || 'Could not save conditions');
                }
              }
            }}
          />
        </div>
      )}

      {/* Step footer */}
      <Card className="shine-card p-3 rounded-xl space-y-2">
        {showMissing && stepMissing.length > 0 && (
          <div className="rounded-md border border-rose-300 bg-rose-50 px-3 py-2 text-xs text-rose-700"
               data-testid="np-missing-banner">
            <div className="font-semibold mb-0.5">Please fill the required fields marked with <span className="text-rose-500">*</span> before continuing:</div>
            <ul className="list-disc ml-4">
              {stepMissing.map((m) => <li key={m.field}>{m.label}</li>)}
            </ul>
          </div>
        )}
        <div className="flex items-center justify-between">
          <Button variant="outline" onClick={back} disabled={step === 0}>
            <ChevronLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <div className="text-xs text-ink-2">
            Step {step + 1} of {STEPS.length}
          </div>
          <Button onClick={next} disabled={step === STEPS.length - 1} data-testid="np-next-btn">
            Next <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </Card>
    </div>
  );
}
